import React, { useState, useEffect } from 'react';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { resetGlobalState, setSearchKeyword } from '../../hooks/useDashboardGlobalVar';
import '../../css/Login.css';

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, register, isAuthenticated, lastLoginCredentials } = useAuthStore();
  const [isSignUp, setIsSignUp] = useState(false);
  const { t } = useTranslation(['auth']);

  // 检查环境变量，决定是否显示社交登录
  const noSocialLogin = process.env.REACT_APP_NO_SOCIAL_LOGIN === 'true';

  // Form state
  const [loginData, setLoginData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });

  const [registerData, setRegisterData] = useState({
    username: '',
    email: '',
    password: ''
  });

  // Error state
  const [loginError, setLoginError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }

    // 恢复上次登录的邮箱
    if (lastLoginCredentials.email) {
      setLoginData(prev => ({
        ...prev,
        email: lastLoginCredentials.email,
        rememberMe: lastLoginCredentials.rememberEmail
      }));
    }

    // 检查URL参数，如果是注册模式则自动切换到注册面板
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('mode') === 'register') {
      setIsSignUp(true);
    }
  }, [isAuthenticated, navigate, location, lastLoginCredentials]);
  
  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    
    // 清除之前的错误
    setLoginError('');
    
    if (!loginData.email || !loginData.password) {
      setLoginError('Please fill in all login fields!');
      return;
    }
    
    setIsLoading(true);
    
    try {
      const result = await login(loginData.email, loginData.password, loginData.rememberMe);
      if (result.success) {
        // 登录成功时，清除所有缓存和全局状态
        try {
          // 清除 sessionStorage 缓存
          sessionStorage.clear();
          console.log('[登录] 已清除所有sessionStorage缓存');
          
          // 设置标志，表示需要清空状态（用于 Dashboard 挂载时检查）
          sessionStorage.setItem('shouldClearStateOnMount', 'true');
          
          // 重置全局状态（包括搜索框内容等）
          resetGlobalState('login-success');
          console.log('[登录] 已重置全局状态');
          
          // 确保搜索框内容被清空
          setSearchKeyword('', 'login-success');
          console.log('[登录] 已清空搜索框内容');
        } catch (error) {
          console.warn('[登录] 清除缓存时出错:', error);
        }
        
        message.success(t('auth:login.login_success'));
        
        // 检查是否有返回URL
        const searchParams = new URLSearchParams(location.search);
        const returnUrl = searchParams.get('returnUrl');
        
        if (returnUrl) {
          console.log('[登录] 重定向到返回URL:', decodeURIComponent(returnUrl));
          navigate(decodeURIComponent(returnUrl));
        } else {
          navigate('/');
        }
      } else {
        // 显示错误信息在页面上
        setLoginError(result.message || 'Login failed, please check your credentials');
      }
    } catch (error) {
      console.error('登录错误:', error);
      setLoginError('An error occurred during login, please try again later');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    
    if (!registerData.username || !registerData.email || !registerData.password) {
      message.error('Please fill in all registration fields!');
      return;
    }
    
    const result = await register(registerData.username, registerData.email, registerData.password);
    if (result.success) {
      // 注册成功后清空注册信息
      setRegisterData({ username: '', email: '', password: '' });
      
      if (result.requiresVerification) {
        const successMessage = result.message || 'Registration Successful! Please check your email and click the verification link to complete registration.';
        
        console.log('[注册成功] 显示成功消息:', successMessage);
        
        message.success(successMessage);
        
        // 自动填充登录邮箱
        setLoginData(prev => ({
          ...prev,
          email: registerData.email
        }));
      } else {
        const successMessage = 'Registration Successful! Please login';
        message.success(successMessage);
        alert(`Registration Successful\n\n${successMessage}`);
        setIsSignUp(false);
        // 自动填充登录邮箱
        setLoginData(prev => ({
          ...prev,
          email: registerData.email
        }));
      }
    } else {
      // 使用多种方式显示错误信息，确保用户能看到
      console.error('Registration failed:', result.message);
      
      // 使用 alert 作为最可靠的显示方式
      alert(`Registration failed\n\n${result.message}`);
      
      // 同时尝试 message.error
      //message.error(result.message);
    }
  };

  const handleSocialLogin = () => {
    // 根据当前URL动态拼接Google登录地址
    const currentOrigin = window.location.origin;
    const apiUrl = process.env.REACT_APP_API_URL || currentOrigin.replace(':3000', ':4000');
    
    // 如果 REACT_APP_API_URL 是完整 URL（包含协议），直接使用
    // 如果是相对路径（如 /api），需要拼接当前 origin
    let backendUrl;
    if (apiUrl.startsWith('http://') || apiUrl.startsWith('https://')) {
      // 完整 URL，直接使用
      backendUrl = apiUrl;
    } else {
      // 相对路径，拼接当前 origin
      backendUrl = `${currentOrigin}${apiUrl}`;
    }
    
    // 确保 URL 以 / 结尾，然后拼接 auth/google（避免重复 /api）
    const googleAuthUrl = `${backendUrl.replace(/\/$/, '')}/auth/google`;
    
    console.log('Google登录地址:', googleAuthUrl);
    window.location.href = googleAuthUrl;
  };

  const handleForgotPassword = () => {
    navigate('/forgot-password');
  };

  const switchToSignUp = () => {
    setIsSignUp(true);
    setLoginError('');
  };

  const switchToSignIn = () => {
    setIsSignUp(false);
    setLoginError('');
  };

  const handleInputChange = (field, value) => {
    setLoginData({...loginData, [field]: value});
    if (loginError) {
      setLoginError('');
    }
  };

  // Google Icon SVG component
  const GoogleIcon = () => (
    <svg className="google-icon" viewBox="0 0 24 24">
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  );

  return (
    <div className="login-page">
      <div className="auth-container">
        <div className={`auth-wrapper ${isSignUp ? 'signup-active' : ''}`}>
          {!isSignUp ? (
            <div className="auth-card">
              {/* Left side - Login Form */}
              <div className="auth-form-panel">
                <div className="auth-form-content">
                  <form onSubmit={handleLoginSubmit} className="auth-form">
                    {/* Google Sign In */}
                    {!noSocialLogin && (
                      <button
                        type="button"
                        className="google-btn"
                        onClick={handleSocialLogin}
                      >
                        <GoogleIcon />
                        Sign in with Google
                      </button>
                    )}

                    <div className="divider">
                      <div className="divider-line" />
                      <span className="divider-text">{t('auth:social.or_use_account')}</span>
                      <div className="divider-line" />
                    </div>

                    {/* Email Input */}
                    <div className="input-group">
                      <input
                        type="email"
                        placeholder={t('auth:login.email')}
                        value={loginData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        className="auth-input"
                        required
                      />
                    </div>

                    {/* Password Input */}
                    <div className="input-group">
                      <input
                        type="password"
                        placeholder={t('auth:login.password')}
                        value={loginData.password}
                        onChange={(e) => handleInputChange('password', e.target.value)}
                        className="auth-input"
                        required
                      />
                    </div>

                    {/* Error Message */}
                    {loginError && (
                      <div className="error-message">
                        {loginError}
                      </div>
                    )}

                    {/* Remember Me */}
                    <div className="remember-me">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={loginData.rememberMe}
                          onChange={(e) => setLoginData({...loginData, rememberMe: e.target.checked})}
                          className="checkbox-input"
                        />
                        <span className="checkbox-custom"></span>
                        <span className="checkbox-text">{t('auth:login.remember_me')}</span>
                      </label>
                    </div>

                    {/* Forgot Password */}
                    <div className="forgot-password-wrapper">
                      <button
                        type="button"
                        className="forgot-password-link"
                        onClick={handleForgotPassword}
                      >
                        {t('auth:login.forgot_password')}
                      </button>
                    </div>

                    {/* Login Button */}
                    <button
                      type="submit"
                      className="submit-btn"
                      disabled={isLoading}
                    >
                      {t('auth:login.submit').toUpperCase()}
                    </button>
                  </form>
                </div>
              </div>

              {/* Right side - Welcome Panel */}
              <div className="auth-welcome-panel">
                <div className="welcome-content">
                  {t('auth:overlay.hello_friend_title') && (
                    <h2 className="welcome-title">{t('auth:overlay.hello_friend_title')}</h2>
                  )}
                  <p className="welcome-desc">{t('auth:overlay.hello_friend_desc')}</p>
                  <button
                    type="button"
                    onClick={switchToSignUp}
                    className="switch-btn"
                  >
                    {t('auth:overlay.sign_up').toUpperCase()}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="auth-card">
              {/* Left side - Welcome Back Panel */}
              <div className="auth-welcome-panel">
                <div className="welcome-content">
                  <h2 className="welcome-title">{t('auth:overlay.welcome_back_title')}</h2>
                  <p className="welcome-desc">{t('auth:overlay.welcome_back_desc')}</p>
                  <button
                    type="button"
                    onClick={switchToSignIn}
                    className="switch-btn"
                  >
                    {t('auth:overlay.sign_in').toUpperCase()}
                  </button>
                </div>
              </div>

              {/* Right side - Registration Form */}
              <div className="auth-form-panel">
                <div className="auth-form-content">
                  <form onSubmit={handleRegisterSubmit} className="auth-form">
                    {/* Google Sign Up */}
                    {!noSocialLogin && (
                      <button
                        type="button"
                        className="google-btn"
                        onClick={handleSocialLogin}
                      >
                        <GoogleIcon />
                        Sign in with Google
                      </button>
                    )}

                    <div className="divider">
                      <div className="divider-line" />
                      <span className="divider-text">{t('auth:social.or_email_register')}</span>
                      <div className="divider-line" />
                    </div>

                    {/* Name Input */}
                    <div className="input-group">
                      <input
                        type="text"
                        placeholder={t('auth:fields.name')}
                        value={registerData.username}
                        onChange={(e) => setRegisterData({...registerData, username: e.target.value})}
                        className="auth-input"
                        required
                      />
                    </div>

                    {/* Email Input */}
                    <div className="input-group">
                      <input
                        type="email"
                        placeholder={t('auth:fields.email')}
                        value={registerData.email}
                        onChange={(e) => setRegisterData({...registerData, email: e.target.value})}
                        className="auth-input"
                        required
                      />
                    </div>

                    {/* Password Input */}
                    <div className="input-group">
                      <input
                        type="password"
                        placeholder={t('auth:fields.password')}
                        value={registerData.password}
                        onChange={(e) => setRegisterData({...registerData, password: e.target.value})}
                        className="auth-input"
                        required
                      />
                    </div>

                    {/* Register Button */}
                    <button
                      type="submit"
                      className="submit-btn"
                    >
                      {t('auth:register.submit').toUpperCase()}
                    </button>
                  </form>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;
